import React, { useState, useEffect, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { Drawer as DrawerPrimitive } from 'vaul'
import { cn } from '../../lib/utils'

function Drawer({ shouldScaleBackground = true, direction = 'right', ...props }) {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    function checkMobile() {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return (
    <DrawerPrimitive.Root
      shouldScaleBackground={shouldScaleBackground}
      direction={isMobile ? 'bottom' : direction}
      {...props}
    />
  )
}
Drawer.displayName = 'Drawer'

Drawer.propTypes = {
  shouldScaleBackground: PropTypes.bool,
  direction: PropTypes.oneOf(['top', 'bottom', 'left', 'right'])
}

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerOverlay = forwardRef(function DrawerOverlay({ className, ...props }, ref) {
  return <DrawerPrimitive.Overlay ref={ref} className={cn('fixed inset-0 z-50 bg-black/80', className)} {...props} />
})
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

DrawerOverlay.propTypes = {
  className: PropTypes.string
}

const DrawerContent = forwardRef(function DrawerContent({ className, children, ...props }, ref) {
  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          'fixed z-50 bg-white border flex flex-col',
          'bottom-0 inset-x-0 max-h-[90vh] rounded-t-[10px] border-t',
          'md:top-0 md:right-0 md:bottom-0 md:left-auto md:h-screen md:max-h-screen md:w-full md:max-w-md md:rounded-none md:border-l md:border-t-0',
          className
        )}
        {...props}
      >
        <div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-gray-200 md:hidden" />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
})
DrawerContent.displayName = 'DrawerContent'

DrawerContent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
}

function DrawerHeader({ className, ...props }) {
  return <div className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)} {...props} />
}
DrawerHeader.displayName = 'DrawerHeader'

DrawerHeader.propTypes = {
  className: PropTypes.string
}

function DrawerFooter({ className, ...props }) {
  return <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
}
DrawerFooter.displayName = 'DrawerFooter'

DrawerFooter.propTypes = {
  className: PropTypes.string
}

const DrawerTitle = forwardRef(function DrawerTitle({ className, ...props }, ref) {
  return (
    <DrawerPrimitive.Title
      ref={ref}
      className={cn('text-lg font-semibold leading-none tracking-tight text-gray-900', className)}
      {...props}
    />
  )
})
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

DrawerTitle.propTypes = {
  className: PropTypes.string
}

const DrawerDescription = forwardRef(function DrawerDescription({ className, ...props }, ref) {
  return <DrawerPrimitive.Description ref={ref} className={cn('text-sm text-gray-500', className)} {...props} />
})
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

DrawerDescription.propTypes = {
  className: PropTypes.string
}

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription
}
